import React, { useCallback, useEffect, useRef, useState } from 'react';
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide } from 'd3-force';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, Network, Search, ZoomIn, ZoomOut } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

function useGraph(symbol) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (sym) => {
    if (!sym) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/director-graph?symbol=${encodeURIComponent(sym)}&depth=1`);
      if (!res.ok) throw new Error(res.status === 404 ? 'Symbol not found' : `HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, load };
}

function getThemeColors() {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    canvasBg: isDark ? '#0f172a' : '#ffffff',
    textPrimary: isDark ? '#e2e8f0' : '#111827',
    textSecondary: isDark ? '#94a3b8' : '#4b5563',
    textStroke: isDark ? '#0f172a' : '#ffffff',
    edgeColor: isDark ? 'rgba(100,116,139,0.3)' : 'rgba(100,116,139,0.4)',
    hoverStroke: isDark ? '#fff' : '#111827',
  };
}

function ForceGraph({ nodes, edges, width, height }) {
  const canvasRef = useRef(null);
  const simRef = useRef(null);
  const transformRef = useRef({ x: 0, y: 0, k: 1 });
  const dragRef = useRef(null);
  const hoveredRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!nodes.length) return;

    const nodeMap = {};
    const simNodes = nodes.map(n => {
      const sn = { ...n, x: width / 2 + Math.random() * 100 - 50, y: height / 2 + Math.random() * 100 - 50 };
      nodeMap[n.id] = sn;
      return sn;
    });
    const simEdges = edges.map(e => ({ ...e, source: nodeMap[e.source], target: nodeMap[e.target] })).filter(e => e.source && e.target);

    const sim = forceSimulation(simNodes)
      .force('link', forceLink(simEdges).id(d => d.id).distance(140))
      .force('charge', forceManyBody().strength(-350))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collide', forceCollide(35));

    simRef.current = sim;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    function strokeText(text, x, y, strokeColor) {
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
    }

    function draw() {
      const t = transformRef.current;
      const c = getThemeColors();
      ctx.save();
      ctx.fillStyle = c.canvasBg;
      ctx.fillRect(0, 0, width, height);
      ctx.translate(t.x, t.y);
      ctx.scale(t.k, t.k);

      for (const e of simEdges) {
        ctx.beginPath();
        ctx.moveTo(e.source.x, e.source.y);
        ctx.lineTo(e.target.x, e.target.y);
        ctx.strokeStyle = c.edgeColor;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }

      const fs = Math.round(11 / Math.max(t.k, 0.5));
      const fsSmall = Math.round(9 / Math.max(t.k, 0.5));

      for (const n of simNodes) {
        const r = n.type === 'company' ? 20 : 12;
        const isHovered = hoveredRef.current === n.id;

        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
        ctx.fillStyle = n.type === 'company'
          ? (isHovered ? '#34d399' : '#10b981')
          : (isHovered ? '#93c5fd' : '#60a5fa');
        ctx.fill();
        if (isHovered) {
          ctx.strokeStyle = c.hoverStroke;
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }

        const count = n.type === 'company' ? n.director_count : n.company_count;
        if (count != null && count > 0) {
          ctx.fillStyle = '#fff';
          ctx.font = `bold ${Math.round(12 / Math.max(t.k, 0.5))}px system-ui`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(count), n.x, n.y);
        }

        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        const label = n.symbol || n.label;
        const maxLen = 18;
        const displayLabel = label.length > maxLen ? label.slice(0, maxLen) + '…' : label;
        ctx.fillStyle = c.textPrimary;
        ctx.font = `600 ${fs}px system-ui`;
        strokeText(displayLabel, n.x, n.y + r + 5, c.textStroke);

        const subLabel = n.type === 'company'
          ? (n.director_count != null ? `${n.director_count} director${n.director_count !== 1 ? 's' : ''}` : '')
          : (n.company_count != null ? `${n.company_count} compan${n.company_count !== 1 ? 'ies' : 'y'}` : '');
        if (subLabel) {
          ctx.fillStyle = c.textSecondary;
          ctx.font = `${fsSmall}px system-ui`;
          strokeText(subLabel, n.x, n.y + r + 5 + fs + 2, c.textStroke);
        }
      }

      ctx.restore();
    }

    sim.on('tick', draw);

    const findNode = (mx, my) => {
      const t = transformRef.current;
      const cx = (mx - t.x) / t.k;
      const cy = (my - t.y) / t.k;
      for (const n of simNodes) {
        const r = n.type === 'company' ? 20 : 12;
        if (Math.hypot(n.x - cx, n.y - cy) < r + 6) return n;
      }
      return null;
    };

    let isPanning = false;
    let lastMouse = null;

    canvas.onmousedown = (e) => {
      const node = findNode(e.offsetX, e.offsetY);
      if (node) {
        dragRef.current = node;
        node.fx = node.x;
        node.fy = node.y;
        sim.alphaTarget(0.3).restart();
      } else {
        isPanning = true;
        lastMouse = { x: e.offsetX, y: e.offsetY };
      }
    };

    canvas.onmousemove = (e) => {
      if (dragRef.current) {
        const t = transformRef.current;
        dragRef.current.fx = (e.offsetX - t.x) / t.k;
        dragRef.current.fy = (e.offsetY - t.y) / t.k;
      } else if (isPanning && lastMouse) {
        transformRef.current.x += e.offsetX - lastMouse.x;
        transformRef.current.y += e.offsetY - lastMouse.y;
        lastMouse = { x: e.offsetX, y: e.offsetY };
        draw();
      } else {
        const node = findNode(e.offsetX, e.offsetY);
        const prevHovered = hoveredRef.current;
        hoveredRef.current = node ? node.id : null;
        canvas.style.cursor = node ? 'pointer' : 'grab';
        if (node) {
          setTooltip({
            x: e.offsetX,
            y: e.offsetY,
            label: node.label,
            type: node.type,
            symbol: node.symbol,
            designation: node.designation,
            director_count: node.director_count,
            company_count: node.company_count,
          });
        } else {
          setTooltip(null);
        }
        if (prevHovered !== hoveredRef.current) draw();
      }
    };

    canvas.onmouseup = () => {
      if (dragRef.current) {
        dragRef.current.fx = null;
        dragRef.current.fy = null;
        dragRef.current = null;
        sim.alphaTarget(0);
      }
      isPanning = false;
      lastMouse = null;
    };

    canvas.onwheel = (e) => {
      e.preventDefault();
      const t = transformRef.current;
      const scale = e.deltaY > 0 ? 0.9 : 1.1;
      const newK = Math.max(0.1, Math.min(5, t.k * scale));
      t.x = e.offsetX - (e.offsetX - t.x) * (newK / t.k);
      t.y = e.offsetY - (e.offsetY - t.y) * (newK / t.k);
      t.k = newK;
      draw();
    };

    return () => { sim.stop(); };
  }, [nodes, edges, width, height]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className="rounded-lg bg-background border border-border"
      />
      {tooltip && (
        <div
          className="absolute pointer-events-none bg-popover border border-border rounded-md shadow-lg px-3 py-2 text-xs z-50"
          style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
        >
          <div className="font-semibold">{tooltip.label}</div>
          {tooltip.designation && (
            <div className="text-muted-foreground text-[10px]">{tooltip.designation}</div>
          )}
          <div className="text-muted-foreground capitalize">
            {tooltip.type}{tooltip.symbol ? ` · ${tooltip.symbol}` : ''}
          </div>
          {tooltip.type === 'company' && tooltip.director_count != null && (
            <div className="text-[10px] mt-0.5">{tooltip.director_count} director{tooltip.director_count !== 1 ? 's' : ''}</div>
          )}
          {tooltip.type === 'director' && tooltip.company_count != null && (
            <div className="text-[10px] mt-0.5">{tooltip.company_count} compan{tooltip.company_count !== 1 ? 'ies' : 'y'}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DirectorNetworkPage() {
  const [inputSymbol, setInputSymbol] = useState('');
  const { data, loading, error, load } = useGraph();
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 900, h: 600 });

  useEffect(() => {
    if (containerRef.current) {
      const w = containerRef.current.clientWidth;
      setDims({ w, h: Math.max(500, window.innerHeight - 280) });
    }
  }, []);

  const onSearch = () => {
    if (inputSymbol.trim()) load(inputSymbol.trim());
  };

  const stats = data ? {
    companies: data.nodes.filter(n => n.type === 'company').length,
    directors: data.nodes.filter(n => n.type === 'director').length,
    connections: data.edges.length,
  } : null;

  return (
    <div className="space-y-4" ref={containerRef}>
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Network className="w-5 h-5 text-emerald-500" /> Director Network
        </h1>
        <p className="text-xs text-muted-foreground">
          Explore board-level connections — who sits on which boards together
        </p>
      </div>

      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1 h-8 px-2 rounded-md border border-border bg-background focus-within:ring-2 focus-within:ring-emerald-500/50 w-52">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={inputSymbol}
              onChange={(e) => setInputSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              placeholder="NSE Symbol (e.g. TCS)"
              className="flex-1 text-xs bg-transparent outline-none"
            />
          </div>
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onSearch} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Explore'}
          </Button>

          {stats && (
            <div className="flex gap-3 ml-auto text-xs text-muted-foreground">
              <span><strong className="text-emerald-500">{stats.companies}</strong> companies</span>
              <span><strong className="text-blue-400">{stats.directors}</strong> directors</span>
              <span><strong>{stats.connections}</strong> connections</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex gap-4 text-xs">
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Company</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-400" /> Director</div>
        <span className="text-muted-foreground ml-2">Scroll to zoom · Drag nodes · Pan background</span>
      </div>

      {error && (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && !loading && (
        <ForceGraph nodes={data.nodes} edges={data.edges} width={dims.w} height={dims.h} />
      )}

      {!data && !loading && !error && (
        <div className="text-center py-20 space-y-2">
          <Network className="w-10 h-10 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground">Enter a symbol above to explore its director network.</p>
        </div>
      )}
    </div>
  );
}
